import { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products, categories } from '@/mocks/products';
import { useCart } from '@/hooks/useCart';

const categoryNameMap: Record<string, string> = {
  ceramics: 'კერამიკა',
  flooring: 'იატაკი',
  'resin-flooring': 'ფრიალი იატაკი',
  ceiling: 'ჭერი',
  facade: 'ფასადი',
  'construction-chemistry': 'სამშენებლო ქიმია',
};

const categoryDescMap: Record<string, string> = {
  ceramics: 'ულტრა-თხელი დიდი ფორმატის კერამიკული ფილები კედლისა, იატაკისა და ფასადისთვის. Laminam-ის ინოვაციური ტექნოლოგია 3მმ-დან 20მმ-მდე სისქით.',
  flooring: 'ლამინირებული პარკეტი, ინჟინერული ხე, SPC და LVT ფილები Fodbo და Dicson ბრენდებისგან. ნებისმიერი სტილისა და ფერის არჩევანი.',
  'resin-flooring': 'ეპოქსიდური და პოლიურეთანის ფრიალი იატაკის სისტემები Ardex და Tecnopol-ისგან კომერციული და საცხოვრებელი გამოყენებისთვის.',
  ceiling: 'გაჭიმული ჭერის სისტემები, ბაფლები, ბეტონის ეფექტი და აკუსტიკური პანელები Armstrong და Knauf-ისგან ნებისმიერი ინტერიერისთვის.',
  facade: 'გარე ფასადის სისტემები, ვენტილირებადი ფასადები და საიზოლაციო მასალები Laminam და Tecnopol-ისგან კომერციული მშენებლობისთვის.',
  'construction-chemistry': 'წებოები, შემმავსებლები, ჰიდროიზოლაცია და სამშენებლო ქიმიური პროდუქტები Ardex და Tecnopol-ისგან პროფესიონალებისთვის.',
};

const categoryHeroImages: Record<string, string> = {
  ceramics: 'https://readdy.ai/api/search-image?query=Large%20format%20Laminam%20ceramic%20tiles%20luxury%20interior%20wall%20floor%20installation%20elegant%20minimal%20space%20warm%20neutral%20tones%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-ceramics&orientation=landscape',
  flooring: 'https://readdy.ai/api/search-image?query=Luxury%20wood%20parquet%20flooring%20elegant%20interior%20warm%20oak%20tones%20minimal%20modern%20space%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-flooring&orientation=landscape',
  'resin-flooring': 'https://readdy.ai/api/search-image?query=Polished%20epoxy%20resin%20floor%20coating%20seamless%20glossy%20surface%20modern%20industrial%20interior%20minimal%20warm%20lighting%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-resin&orientation=landscape',
  ceiling: 'https://readdy.ai/api/search-image?query=Modern%20suspended%20ceiling%20system%20acoustic%20panels%20elegant%20interior%20design%20warm%20lighting%20minimal%20architecture%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-ceiling&orientation=landscape',
  facade: 'https://readdy.ai/api/search-image?query=Modern%20building%20exterior%20facade%20cladding%20system%20elegant%20architecture%20minimal%20design%20warm%20neutral%20tones%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-facade&orientation=landscape',
  'construction-chemistry': 'https://readdy.ai/api/search-image?query=Professional%20construction%20chemicals%20adhesives%20grout%20products%20display%20minimal%20clean%20background%20warm%20lighting%20professional%20photography%20wide%20angle&width=1920&height=800&seq=cat-hero-chem&orientation=landscape',
};

const sortOptions = [
  { value: 'default', label: 'ნაგულისხმევი' },
  { value: 'price-asc', label: 'ფასი: დაბალი → მაღალი' },
  { value: 'price-desc', label: 'ფასი: მაღალი → დაბალი' },
  { value: 'name', label: 'სახელი: A-Z' },
];

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const [sortBy, setSortBy] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [addedId, setAddedId] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const categoryName = id ? categoryNameMap[id] : null;
  const categoryData = categories.find((c) => c.id === id);

  useEffect(() => {
    if (id && !categoryNameMap[id]) {
      navigate('/categories');
    }
  }, [id, navigate]);

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => p.category === categoryName);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [categoryName, sortBy, searchQuery]);

  const relatedCategories = categories.filter((c) => c.id !== id).slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filteredProducts]);

  const handleAddToCart = (e: React.MouseEvent, product: typeof products[0]) => {
    e.preventDefault();
    addItem(product);
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const handleBuyNow = (e: React.MouseEvent, product: typeof products[0]) => {
    e.preventDefault();
    addItem(product);
    openCart();
  };

  if (!categoryName) return null;

  const heroImage = id ? categoryHeroImages[id] : '';
  const heroDesc = id ? categoryDescMap[id] : '';

  return (
    <div className="font-sans bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[480px] md:min-h-[560px] bg-[#1C1C1C] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={categoryName}
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-white/30 mb-6">
            <Link to="/" className="hover:text-white/60 transition-colors">მთავარი</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/categories" className="hover:text-white/60 transition-colors">კატეგორიები</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#ec3145]">{categoryName}</span>
          </div>

          <div className="max-w-2xl">
            <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-3">
              {categoryData?.brands.join(' · ')}
            </span>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              {categoryName}
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-lg mb-8">
              {heroDesc}
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white/40 text-sm">
                <span className="text-white font-bold text-xl">{filteredProducts.length}</span> პროდუქტი
              </span>
              {categoryData && (
                <span className="text-white/40 text-sm">
                  <span className="text-white font-bold text-xl">{categoryData.count}+</span> ასორტიმენტი
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <div className="bg-white border-b border-[#1C1C1C]/8 sticky top-[64px] z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#1C1C1C]/30 text-sm"></i>
              <input
                type="text"
                placeholder="ძიება..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-[#1C1C1C]/10 bg-[#F5F2EE] text-[#1C1C1C] placeholder-[#1C1C1C]/30 focus:outline-none focus:border-[#ec3145] transition-colors w-48 md:w-64"
              />
            </div>
            <span className="text-sm text-[#1C1C1C]/40 whitespace-nowrap">
              {filteredProducts.length} პროდუქტი
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center border border-[#1C1C1C]/10">
              <button
                onClick={() => setViewMode('grid')}
                className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${
                  viewMode === 'grid' ? 'bg-[#1C1C1C] text-white' : 'text-[#1C1C1C]/40 hover:text-[#1C1C1C]'
                }`}
              >
                <i className="ri-grid-line"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${
                  viewMode === 'list' ? 'bg-[#1C1C1C] text-white' : 'text-[#1C1C1C]/40 hover:text-[#1C1C1C]'
                }`}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-[#1C1C1C]/10 bg-white px-3 py-2 text-[#1C1C1C] focus:outline-none focus:border-[#ec3145] cursor-pointer"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products */}
      <section className="bg-[#F5F2EE] py-12 md:py-16" ref={sectionRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-24">
              <i className="ri-search-line text-5xl text-[#1C1C1C]/15 mb-5 block"></i>
              <h3 className="font-serif text-2xl font-bold text-[#1C1C1C] mb-2">
                პროდუქტები ვერ მოიძებნა
              </h3>
              <p className="text-sm text-[#1C1C1C]/40 mb-6">სცადეთ სხვა საძიებო სიტყვა</p>
              <button
                onClick={() => setSearchQuery('')}
                className="inline-flex items-center gap-2 bg-[#ec3145] text-white font-semibold px-6 py-3 hover:bg-[#c9283a] transition-colors cursor-pointer whitespace-nowrap"
              >
                ძიების გასუფთავება
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="reveal opacity-0 group bg-white overflow-hidden"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <Link to={`/product/${product.id}`} className="block">
                    <div className="relative h-60 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {product.tag && (
                        <div className="absolute top-4 left-4 bg-[#ec3145] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1">
                          {product.tag}
                        </div>
                      )}
                    </div>
                    <div className="p-5 pb-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#ec3145]">
                          {product.brand}
                        </span>
                        <span className="text-[10px] text-[#1C1C1C]/30">{product.origin}</span>
                      </div>
                      <h3 className="font-serif text-lg font-bold text-[#1C1C1C] mb-1.5 group-hover:text-[#ec3145] transition-colors duration-300 leading-snug">
                        {product.name}
                      </h3>
                      <p className="text-xs text-[#1C1C1C]/40 leading-relaxed line-clamp-2 mb-3">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-[#1C1C1C]/5">
                        <span className="font-serif text-xl font-bold text-[#1C1C1C]">
                          ₾{product.price}
                        </span>
                        <span className="text-xs text-[#1C1C1C]/30">
                          {product.size}
                        </span>
                      </div>
                    </div>
                  </Link>
                  {/* Quick actions */}
                  <div className="px-5 pb-5 flex gap-2">
                    <button
                      onClick={(e) => handleAddToCart(e, product)}
                      className={`flex-1 py-2 text-xs font-bold tracking-[0.1em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        addedId === product.id
                          ? 'bg-green-500 text-white'
                          : 'bg-[#1C1C1C] text-white hover:bg-[#ec3145]'
                      }`}
                    >
                      {addedId === product.id ? (
                        <><i className="ri-check-line mr-1"></i>დამატებულია</>
                      ) : (
                        <><i className="ri-shopping-cart-line mr-1"></i>კალათა</>
                      )}
                    </button>
                    <button
                      onClick={(e) => handleBuyNow(e, product)}
                      className="px-3 py-2 text-xs font-bold border border-[#1C1C1C]/15 text-[#1C1C1C]/60 hover:border-[#ec3145] hover:text-[#ec3145] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      ყიდვა
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="reveal opacity-0 group bg-white overflow-hidden"
                  style={{ animationDelay: `${index * 0.04}s` }}
                >
                  <div className="flex flex-col sm:flex-row">
                    <Link to={`/product/${product.id}`} className="relative w-full sm:w-52 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {product.tag && (
                        <div className="absolute top-3 left-3 bg-[#ec3145] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1">
                          {product.tag}
                        </div>
                      )}
                    </Link>
                    <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#ec3145]">{product.brand}</span>
                          <span className="text-[10px] text-[#1C1C1C]/30">{product.origin}</span>
                        </div>
                        <Link to={`/product/${product.id}`}>
                          <h3 className="font-serif text-xl font-bold text-[#1C1C1C] mb-2 group-hover:text-[#ec3145] transition-colors duration-300">
                            {product.name}
                          </h3>
                        </Link>
                        <p className="text-sm text-[#1C1C1C]/50 leading-relaxed mb-3">
                          {product.description}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#1C1C1C]/30">
                          {product.size && <span>ზომა: {product.size}</span>}
                          {product.thickness && <span>სისქე: {product.thickness}</span>}
                          {product.material && <span>მასალა: {product.material}</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1C1C1C]/5">
                        <span className="font-serif text-2xl font-bold text-[#1C1C1C]">₾{product.price}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => handleAddToCart(e, product)}
                            className={`px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                              addedId === product.id
                                ? 'bg-green-500 text-white'
                                : 'bg-[#1C1C1C] text-white hover:bg-[#ec3145]'
                            }`}
                          >
                            {addedId === product.id ? <><i className="ri-check-line mr-1"></i>დამატებულია</> : <><i className="ri-shopping-cart-line mr-1"></i>კალათა</>}
                          </button>
                          <button
                            onClick={(e) => handleBuyNow(e, product)}
                            className="px-4 py-2 text-xs font-bold border border-[#1C1C1C]/15 text-[#1C1C1C]/60 hover:border-[#ec3145] hover:text-[#ec3145] transition-all duration-300 cursor-pointer whitespace-nowrap"
                          >
                            ყიდვა
                          </button>
                          <Link
                            to={`/product/${product.id}`}
                            className="px-4 py-2 text-xs font-bold border border-[#ec3145] text-[#ec3145] hover:bg-[#ec3145] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                          >
                            დეტალურად
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related Categories */}
      {relatedCategories.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-10 pb-6 border-b border-[#1C1C1C]/8">
              <div>
                <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-2">სხვა</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1C1C]">კატეგორიები</h2>
              </div>
              <Link
                to="/categories"
                className="hidden md:inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C]/40 hover:text-[#ec3145] transition-colors whitespace-nowrap"
              >
                ყველა კატეგორია <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="group relative overflow-hidden h-56"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/80 via-[#1C1C1C]/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/40 mb-1">
                      {cat.brands.join(' · ')}
                    </div>
                    <h3 className="font-serif text-xl font-bold text-white group-hover:text-[#ec3145] transition-colors duration-300">
                      {cat.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
